import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Edit, UserMinus, Eye } from "lucide-react";
import { supabase } from "../../lib/supabase";
import {
  inferPaymentStatus,
  getStatusBadgeColor,
  getStatusDisplayName,
  type PaymentStatus,
} from "../../utils/payments";
import toast from "react-hot-toast";

interface Student {
  id: string;
  codername: string;
  age: number;
  parentname: string;
  email: string;
  phone: string;
  run_id: string | null;
}

interface Run {
  id: string;
  price: number;
  series: {
    total_labs: number;
    labs?: {
      id: string;
      order_number: number;
      title: string;
    }[];
  };
  lab_dates?: {
    id: string;
    run_id: string;
    lab_number: number;
    date: string;
  }[];
}

interface Attendance {
  student_id: string;
  lab_number: number;
  is_present: boolean;
}

interface Payment {
  id: string;
  student_id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  notes: string | null;
}

interface Props {
  students: Student[];
  isAdmin: boolean;
  showActions?: boolean;
  onDeleteStudent?: (id: string) => void;
  run: Run;
}

export default function RunStudentsTable({
  students,
  isAdmin,
  showActions = true,
  onDeleteStudent,
  run,
}: Props) {
  const [attendance, setAttendance] = useState<Record<string, Attendance[]>>(
    {}
  );
  const [payments, setPayments] = useState<Record<string, Payment>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      // Fetch attendance
      const { data: attendanceData, error: attendanceError } = await supabase
        .from("attendance")
        .select("*")
        .eq("run_id", run.id);

      if (attendanceError) {
        toast.error("Failed to load attendance data");
        return;
      }

      // Fetch current payment amounts
      const { data: paymentData, error: paymentError } = await supabase
        .from("payments")
        .select("*")
        .eq("run_id", run.id);

      if (paymentError) {
        toast.error("Failed to load payment data");
        return;
      }

      // Process attendance data
      const attendanceByStudent: Record<string, Attendance[]> = {};
      attendanceData?.forEach((record) => {
        if (!attendanceByStudent[record.student_id]) {
          attendanceByStudent[record.student_id] = [];
        }
        attendanceByStudent[record.student_id].push({
          student_id: record.student_id,
          lab_number: record.lab_number,
          is_present: record.is_present,
        });
      });

      // Store full payment objects per student
      const currentPayments: Record<string, Payment> = {};
      paymentData?.forEach((record) => {
        currentPayments[record.student_id] = {
          id: record.id,
          student_id: record.student_id,
          amount: record.amount,
          payment_date: record.payment_date,
          payment_method: record.payment_method,
          notes: record.notes,
        };
      });

      setAttendance(attendanceByStudent);
      setPayments(currentPayments);
      setIsLoading(false);
    };

    fetchData();
  }, [run.id]);

  const handleAttendanceChange = async (
    studentId: string,
    labNumber: number,
    isPresent: boolean
  ) => {
    try {
      const { error } = await supabase.from("attendance").upsert(
        {
          student_id: studentId,
          run_id: run.id,
          lab_number: labNumber,
          is_present: isPresent,
        },
        {
          onConflict: "student_id,run_id,lab_number",
        }
      );

      if (error) throw error;

      // Update local state
      setAttendance((prev) => ({
        ...prev,
        [studentId]: [
          ...(prev[studentId] || []).filter((a) => a.lab_number !== labNumber),
          {
            student_id: studentId,
            lab_number: labNumber,
            is_present: isPresent,
          },
        ],
      }));

      toast.success("Attendance updated");
    } catch (error) {
      console.error("Error updating attendance:", error);
      toast.error("Failed to update attendance");
    }
  };

  const getAttendance = (studentId: string, labNumber: number) => {
    return (
      attendance[studentId]?.find((a) => a.lab_number === labNumber)
        ?.is_present || false
    );
  };

  const handlePaymentClick = async (
    studentId: string,
    currentAmount: number
  ) => {
    const newAmount = window.prompt(
      `Enter payment amount (current: $${currentAmount})`,
      String(currentAmount || run.price)
    );

    if (newAmount === null) return; // User cancelled

    const amount = parseFloat(newAmount);
    if (isNaN(amount)) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      // Use upsert to update existing payment record or create new one
      const { error } = await supabase.from("payments").upsert(
        {
          student_id: studentId,
          run_id: run.id,
          amount: amount,
          payment_method: "stripe",
          payment_date: new Date().toISOString().split("T")[0],
          notes: amount === 0 ? "Payment not yet received" : null,
        },
        {
          onConflict: "student_id,run_id",
        }
      );

      if (error) throw error;

      // Update local state with new payment object
      setPayments((prev) => ({
        ...prev,
        [studentId]: {
          id: prev[studentId]?.id || "", // Will be updated on next fetch
          student_id: studentId,
          amount: amount,
          payment_method: "stripe",
          payment_date: new Date().toISOString().split("T")[0],
          notes: amount === 0 ? "Payment not yet received" : null,
        },
      }));

      toast.success(
        amount > 0 ? "Payment updated" : "Payment marked as not received"
      );
    } catch (error) {
      console.error("Error updating payment:", error);
      toast.error("Failed to update payment");
    }
  };

  const getPaymentStatus = (studentId: string): PaymentStatus => {
    const payment = payments[studentId];
    if (!payment) {
      return "not_received";
    }
    return inferPaymentStatus(payment, run.price);
  };

  const getPaymentAmount = (studentId: string): number => {
    return payments[studentId]?.amount || 0;
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-neutral-200">
        <thead className="bg-neutral-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
              Student
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
              Payment Status
            </th>
            {Array.from({ length: run.series.total_labs }, (_, i) => {
              const labNumber = i + 1;
              const hasDate = run.lab_dates?.some(
                (d) => d.lab_number === labNumber && d.date
              );
              return (
                <th
                  key={i}
                  className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider ${
                    !hasDate
                      ? "text-neutral-400 bg-neutral-100"
                      : "text-neutral-500"
                  }`}
                >
                  Lab {labNumber}
                </th>
              );
            })}
            {showActions && (
              <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-neutral-200">
          {students.map((student) => {
            const paymentStatus = getPaymentStatus(student.id);
            const paidAmount = getPaymentAmount(student.id);

            return (
              <tr key={student.id} className="hover:bg-neutral-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <Link to={`/students/${student.id}`} className="font-medium">
                    {student.codername}
                  </Link>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {isAdmin ? (
                    <button
                      onClick={() => handlePaymentClick(student.id, paidAmount)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm ${getStatusBadgeColor(
                        paymentStatus
                      )}`}
                    >
                      <span className="mr-1">
                        {getStatusDisplayName(paymentStatus)}
                      </span>
                      <span className="text-sm">
                        (${paidAmount.toFixed(2)} / $
                        {(run?.price || 0).toFixed(2)})
                      </span>
                    </button>
                  ) : (
                    <div
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm ${getStatusBadgeColor(
                        paymentStatus
                      )}`}
                    >
                      <span className="mr-1">
                        {getStatusDisplayName(paymentStatus)}
                      </span>
                      <span className="text-sm">
                        (${paidAmount.toFixed(2)} / $
                        {(run?.price || 0).toFixed(2)})
                      </span>
                    </div>
                  )}
                </td>
                {Array.from({ length: run.series.total_labs }, (_, i) => {
                  const labNumber = i + 1;
                  const hasDate = run.lab_dates?.some(
                    (d) => d.lab_number === labNumber && d.date
                  );
                  return (
                    <td
                      key={i}
                      className={`px-2 py-4 text-center ${
                        !hasDate ? "bg-neutral-100" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={getAttendance(student.id, labNumber)}
                        onChange={(e) =>
                          handleAttendanceChange(
                            student.id,
                            labNumber,
                            e.target.checked
                          )
                        }
                        disabled={!isAdmin || isLoading || !hasDate}
                        className={`form-checkbox h-4 w-4 rounded border-neutral-300 text-secondary focus:ring-0 focus:ring-secondary-500 focus:ring-offset-0 disabled:cursor-not-allowed transition-colors duration-200 ${
                          !hasDate
                            ? "disabled:text-neutral-200 disabled:bg-neutral-100 disabled:border-neutral-200"
                            : "disabled:text-neutral-200"
                        }`}
                      />
                    </td>
                  );
                })}
                {showActions && (
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <Link
                        to={`/students/${student.id}`}
                        className="icon-view"
                        title="View student details"
                      >
                        <Eye className="h-5 w-5" />
                      </Link>
                      {isAdmin && (
                        <>
                          <Link
                            to={`/students/${student.id}/edit`}
                            className="icon-edit"
                            title="Edit student"
                          >
                            <Edit className="h-5 w-5" />
                          </Link>
                          {onDeleteStudent && (
                            <button
                              onClick={() => onDeleteStudent(student.id)}
                              className="icon-edit"
                              title="Remove from group"
                            >
                              <UserMinus className="h-5 w-5" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
